import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Trash, UserCircle, CurrencyInr, HandCoins, ChartLineUp,
  ArrowLeft, Wallet, Phone, Briefcase, X as XIcon, PencilSimple,
  Warning, Receipt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { istToday } from "@/lib/istDate";

const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s.length === 10 ? s + "T00:00:00" : s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch (_e) { return s; }
};
const monthKey = () => new Date().toISOString().slice(0, 7);

const KIND_META = {
  salary:              { label: "SALARY",     color: "text-emerald-300 border-emerald-500/50 bg-emerald-500/10" },
  advance:             { label: "ADVANCE",    color: "text-amber-300 border-amber-500/50 bg-amber-500/10" },
  commission:          { label: "COMMISSION", color: "text-cyan-300 border-cyan-500/50 bg-cyan-500/10" },
  advance_adjustment:  { label: "ADV DEDUCT", color: "text-slate-300 border-slate-500/50 bg-slate-500/10" },
};

export default function StaffPayrollPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "secondary_admin";
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/staff"),
        api.get("/staff/summary").catch(() => ({ data: null })),
      ]);
      setRows(list);
      setSummary(sum);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (detailId) {
    return (
      <StaffDetail
        staffId={detailId}
        onBack={() => { setDetailId(null); load(); }}
        onEditProfile={(s) => setEditing(s)}
        canEdit={isAdmin}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="staff-payroll-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// PAYROLL</div>
          <h1 className="font-heading text-3xl font-black text-white">Staff Salary &amp; Advance</h1>
          <p className="text-slate-400 text-sm mt-1">
            Har staff ka salary, advance aur commission ek jagah — Accounts se auto integrated.
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setAddOpen(true)}
            className="btn-industrial rounded-sm h-11 px-5 font-heading tracking-widest"
            data-testid="new-staff-btn"
          >
            <Plus size={16} className="mr-1" weight="bold" /> ADD STAFF
          </Button>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="ACTIVE STAFF" value={summary.active_staff} icon={UserCircle} tone="cyan" />
          <SummaryCard label="ADVANCE OUTSTANDING" value={inr(summary.total_advance_outstanding)} icon={Warning} tone="amber" />
          <SummaryCard label={`SALARY · ${summary.this_month}`} value={inr(summary.salary_paid_this_month)} icon={CurrencyInr} tone="emerald" />
          <SummaryCard label={`COMMISSION · ${summary.this_month}`} value={inr(summary.commission_paid_this_month)} icon={ChartLineUp} tone="orange" />
        </div>
      )}

      <div className="surface rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-800 text-[10px] font-mono-num tracking-widest text-slate-500">
          <div className="col-span-3">NAME · DESIGNATION</div>
          <div className="col-span-2">MOBILE</div>
          <div className="col-span-2 text-right">BASIC SALARY</div>
          <div className="col-span-2 text-right">ADVANCE OUT</div>
          <div className="col-span-2 text-right">SALARY PAID</div>
          <div className="col-span-1 text-right">ACTIONS</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <UserCircle size={40} className="mx-auto mb-3 text-slate-700" />
            Koi staff nahi. Tap <b className="text-orange-400">+ ADD STAFF</b>
          </div>
        ) : rows.map((s) => (
          <button
            key={s.id}
            onClick={() => setDetailId(s.id)}
            data-testid={`staff-row-${s.id}`}
            className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-800 hover:bg-slate-800/40 items-center w-full text-left"
          >
            <div className="col-span-3">
              <div className="text-white text-sm font-semibold truncate">{s.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{s.designation || "—"}</div>
            </div>
            <div className="col-span-2 font-mono-num text-cyan-300 text-sm">{s.mobile || "—"}</div>
            <div className="col-span-2 text-right font-mono-num text-slate-200">
              {inr(s.basic_salary)}
            </div>
            <div className={"col-span-2 text-right font-mono-num font-bold " + (s.balances?.advance_outstanding > 0 ? "text-amber-300" : "text-slate-500")}>
              {inr(s.balances?.advance_outstanding || 0)}
            </div>
            <div className="col-span-2 text-right font-mono-num text-emerald-300">
              {inr(s.balances?.salary_paid || 0)}
            </div>
            <div className="col-span-1 text-right text-slate-500">
              <ArrowLeft size={14} className="rotate-180 inline" />
            </div>
          </button>
        ))}
      </div>

      <StaffFormDialog
        open={addOpen || !!editing}
        editing={editing}
        onClose={() => { setAddOpen(false); setEditing(null); }}
        onSaved={() => { setAddOpen(false); setEditing(null); load(); }}
      />
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, tone }) {
  const toneCls = {
    cyan: "text-cyan-300 border-cyan-500/40",
    amber: "text-amber-300 border-amber-500/40",
    emerald: "text-emerald-300 border-emerald-500/40",
    orange: "text-orange-300 border-orange-500/40",
  }[tone] || "text-slate-300";
  return (
    <div className="surface rounded-sm p-3 border-l-2" style={{ borderLeftColor: "currentColor" }}>
      <div className="flex items-center gap-2">
        <Icon size={14} className={toneCls} weight="fill" />
        <div className="text-[10px] font-mono-num tracking-widest text-slate-500">{label}</div>
      </div>
      <div className={"font-mono-num font-black text-2xl mt-1 " + toneCls}>{value}</div>
    </div>
  );
}

/* -------------------- Add / Edit staff dialog -------------------- */
function StaffFormDialog({ open, editing, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [designation, setDesignation] = useState("");
  const [basicSalary, setBasicSalary] = useState("");
  const [openingAdvance, setOpeningAdvance] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name || "");
      setMobile(editing.mobile || "");
      setDesignation(editing.designation || "");
      setBasicSalary(String(editing.basic_salary || ""));
      setOpeningAdvance("");
      setNotes(editing.notes || "");
    } else {
      setName(""); setMobile(""); setDesignation("");
      setBasicSalary(""); setOpeningAdvance(""); setNotes("");
    }
  }, [open, editing]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        mobile: mobile.trim(),
        designation: designation.trim(),
        basic_salary: Number(basicSalary) || 0,
        notes: notes.trim(),
      };
      if (editing) {
        await api.patch(`/staff/${editing.id}`, body);
        toast.success("Staff updated");
      } else {
        body.opening_advance = Number(openingAdvance) || 0;
        await api.post("/staff", body);
        toast.success("Staff added");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-lg bg-slate-900 border-slate-700 text-white rounded-sm max-h-[92vh] overflow-y-auto"
        data-testid="staff-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading tracking-widest">
            <UserCircle size={20} className="text-orange-400" weight="fill" />
            {editing ? "EDIT STAFF" : "ADD STAFF"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">NAME</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5"
              placeholder="e.g. Ramesh Kumar" data-testid="staff-name-input" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">MOBILE (OPTIONAL)</Label>
              <Input value={mobile} onChange={(e) => setMobile(e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5"
                placeholder="10 digits" data-testid="staff-mobile-input" />
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">DESIGNATION</Label>
              <Input value={designation} onChange={(e) => setDesignation(e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5"
                placeholder="e.g. Sales Boy / Helper" data-testid="staff-designation-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">BASIC SALARY (₹ / MONTH)</Label>
              <Input type="number" min="0" step="1" value={basicSalary} onChange={(e) => setBasicSalary(e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5 font-mono-num"
                placeholder="15000" data-testid="staff-salary-input" />
            </div>
            {!editing && (
              <div>
                <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">OPENING ADVANCE (₹)</Label>
                <Input type="number" min="0" step="1" value={openingAdvance} onChange={(e) => setOpeningAdvance(e.target.value)}
                  className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5 font-mono-num"
                  placeholder="0" data-testid="staff-opening-adv-input" />
                <div className="text-[10px] text-slate-500 mt-1">
                  Purana bakaya advance jo carry forward karna hai
                </div>
              </div>
            )}
          </div>
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">NOTES</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="bg-slate-800/60 border-slate-600 text-white rounded-sm mt-1.5"
              placeholder="Anything to remember…" data-testid="staff-notes-input" />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" onClick={onClose} variant="outline" className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800 h-11">Cancel</Button>
            <Button type="submit" disabled={busy}
              className="btn-industrial rounded-sm h-11 px-6 font-heading tracking-widest"
              data-testid="staff-submit">
              {busy ? "SAVING…" : (editing ? "SAVE" : "ADD")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Staff detail: ledger + pay buttons -------------------- */
function StaffDetail({ staffId, onBack, onEditProfile, canEdit }) {
  const [staff, setStaff] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payKind, setPayKind] = useState(null); // salary | advance | commission

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/staff/${staffId}/ledger`);
      setStaff(data.staff);
      setEntries(data.entries);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [staffId]);

  const removeEntry = async (e) => {
    if (!window.confirm(`Reverse ${e.kind} of ${inr(e.amount)}?`)) return;
    try {
      await api.delete(`/staff/ledger/${e.id}`);
      toast.success("Reversed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (loading || !staff) return <div className="p-10 text-center text-slate-500">Loading…</div>;

  const bal = staff.balances || {};

  return (
    <div className="space-y-4" data-testid="staff-detail">
      <div className="flex items-center gap-3">
        <Button onClick={onBack} variant="outline" size="sm" data-testid="staff-detail-back"
          className="border-slate-600 text-slate-300 hover:bg-slate-800 h-10 rounded-sm">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
        <div className="flex-1">
          <div className="text-[10px] font-mono-num tracking-widest text-slate-400">// STAFF · {staff.designation || "TEAM"}</div>
          <h1 className="font-heading text-2xl font-black text-white">{staff.name}</h1>
          <div className="text-[11px] font-mono-num text-slate-500 flex items-center gap-3 mt-0.5">
            {staff.mobile && <span className="flex items-center gap-1 text-cyan-300"><Phone size={11} /> {staff.mobile}</span>}
            <span className="flex items-center gap-1"><Briefcase size={11} /> Basic {inr(staff.basic_salary)}</span>
          </div>
        </div>
        {canEdit && (
          <Button onClick={() => onEditProfile(staff)} variant="outline" size="sm"
            className="border-slate-600 text-slate-300 hover:bg-slate-800 h-10 rounded-sm"
            data-testid="staff-edit-profile">
            <PencilSimple size={14} className="mr-1" /> Edit
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="ADVANCE OUTSTANDING" value={inr(bal.advance_outstanding)} icon={Warning} tone={bal.advance_outstanding > 0 ? "amber" : "cyan"} />
        <SummaryCard label="TOTAL ADVANCE GIVEN" value={inr(bal.advance_given)} icon={HandCoins} tone="orange" />
        <SummaryCard label="TOTAL SALARY PAID" value={inr(bal.salary_paid)} icon={CurrencyInr} tone="emerald" />
        <SummaryCard label="TOTAL COMMISSION" value={inr(bal.commission_paid)} icon={ChartLineUp} tone="cyan" />
      </div>

      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setPayKind("salary")} data-testid="pay-salary-btn"
            className="btn-industrial rounded-sm h-11 px-5 font-heading tracking-widest">
            <CurrencyInr size={16} className="mr-1" weight="bold" /> PAY SALARY
          </Button>
          <Button onClick={() => setPayKind("advance")} data-testid="pay-advance-btn"
            className="rounded-sm h-11 px-5 font-heading tracking-widest bg-amber-500 hover:bg-amber-400 text-slate-900">
            <HandCoins size={16} className="mr-1" weight="bold" /> GIVE ADVANCE
          </Button>
          <Button onClick={() => setPayKind("commission")} data-testid="pay-commission-btn"
            className="rounded-sm h-11 px-5 font-heading tracking-widest bg-cyan-500 hover:bg-cyan-400 text-slate-900">
            <ChartLineUp size={16} className="mr-1" weight="bold" /> COMMISSION
          </Button>
        </div>
      )}

      <div className="surface rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-800 text-[10px] font-mono-num tracking-widest text-slate-500 flex items-center gap-2">
          <Receipt size={12} /> LEDGER · {entries.length} EVENTS
        </div>
        {entries.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Koi entry nahi — pehla salary/advance record karo</div>
        ) : entries.map((e) => {
          const meta = KIND_META[e.kind] || KIND_META.salary;
          return (
            <div key={e.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-800 items-center" data-testid={`ledger-row-${e.id}`}>
              <div className="col-span-2">
                <span className={`text-[10px] font-mono-num tracking-widest border px-1.5 py-0.5 rounded-sm ${meta.color}`}>
                  {meta.label}
                </span>
              </div>
              <div className="col-span-2 font-mono-num text-slate-400 text-xs">{fmtDate(e.date)}</div>
              <div className="col-span-5 min-w-0">
                <div className="text-sm text-slate-200 truncate">
                  {e.note || (e.month ? `Salary · ${e.month}` : (e.linked_order ? `Order #${e.linked_order.order_number}` : "—"))}
                </div>
                <div className="text-[10px] font-mono-num text-slate-500 truncate">
                  {e.financial_account_name && <>from {e.financial_account_name} · </>}
                  {e.linked_order && <>#{e.linked_order.order_number} · {e.linked_order.customer_name} · </>}
                  {e.commission_pct ? `${e.commission_pct}% · ` : ""}
                  {e.is_opening ? "opening balance" : ""}
                </div>
              </div>
              <div className={"col-span-2 text-right font-mono-num font-bold " + (e.kind === "advance" ? "text-amber-300" : e.kind === "advance_adjustment" ? "text-slate-400" : e.kind === "commission" ? "text-cyan-300" : "text-emerald-300")}>
                {e.kind === "advance_adjustment" ? "-" : ""}{inr(e.amount)}
              </div>
              <div className="col-span-1 text-right">
                {canEdit && !e.is_opening && (
                  <Button onClick={() => removeEntry(e)} variant="outline" size="sm"
                    className="h-8 w-8 p-0 border-red-500/40 text-red-400 hover:bg-red-500/10"
                    data-testid={`ledger-delete-${e.id}`} title="Reverse">
                    <Trash size={13} />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <PayDialog
        open={!!payKind}
        kind={payKind}
        staff={staff}
        onClose={() => setPayKind(null)}
        onDone={() => { setPayKind(null); load(); }}
      />
    </div>
  );
}

/* -------------------- Pay dialog (salary / advance / commission) -------------------- */
function PayDialog({ open, kind, staff, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(istToday());
  const [note, setNote] = useState("");
  const [month, setMonth] = useState(monthKey());
  const [adjustFromAdv, setAdjustFromAdv] = useState("");
  const [commissionPct, setCommissionPct] = useState("");
  const [linkedOrderId, setLinkedOrderId] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [orderResults, setOrderResults] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [financialAccountId, setFinancialAccountId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAmount(kind === "salary" ? String(staff?.basic_salary || "") : "");
    setDate(istToday());
    setNote(""); setAdjustFromAdv(""); setCommissionPct("");
    setLinkedOrderId(""); setOrderSearch(""); setOrderResults([]);
    setMonth(monthKey());
    setPaymentMethod("Cash");
    (async () => {
      try {
        const { data } = await api.get("/accounts/financial-accounts");
        setAccounts(data || []);
        if (data?.length) setFinancialAccountId(data[0].id);
      } catch (_e) { /* ignore */ }
    })();
  }, [open, kind, staff]);

  // Order search (commission mode)
  useEffect(() => {
    if (kind !== "commission" || !orderSearch.trim()) { setOrderResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/orders?search=${encodeURIComponent(orderSearch)}&limit=8`);
        setOrderResults(Array.isArray(data) ? data.slice(0, 8) : (data?.orders || []).slice(0, 8));
      } catch (_e) { setOrderResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [orderSearch, kind]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount) || 0;
    if (amt <= 0) return toast.error("Amount > 0 chahiye");
    if (!financialAccountId) return toast.error("Financial account select karo");
    setBusy(true);
    try {
      const body = {
        kind,
        amount: amt,
        date,
        financial_account_id: financialAccountId,
        payment_method: paymentMethod,
        note: note.trim(),
      };
      if (kind === "salary") {
        body.month = month;
        const adj = Number(adjustFromAdv) || 0;
        if (adj > 0) body.adjust_from_advance = adj;
      } else if (kind === "commission") {
        if (linkedOrderId) body.linked_order_id = linkedOrderId;
        if (commissionPct) body.commission_pct = Number(commissionPct) || null;
      }
      await api.post(`/staff/${staff.id}/pay`, body);
      toast.success(`${kind.toUpperCase()} recorded`);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const title = kind === "salary" ? "PAY SALARY" : kind === "advance" ? "GIVE ADVANCE" : "PAY COMMISSION";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-md bg-slate-900 border-slate-700 text-white rounded-sm max-h-[92vh] overflow-y-auto"
        data-testid="staff-pay-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading tracking-widest">
            <Wallet size={20} className="text-orange-400" weight="fill" /> {title}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="text-sm text-slate-300 border-l-2 border-orange-500 pl-2">
            {staff?.name} · {staff?.designation || "Staff"}
          </div>
          {kind === "salary" && (
            <div className="text-[11px] text-amber-300">
              Advance outstanding: <b>{inr(staff?.balances?.advance_outstanding || 0)}</b>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">AMOUNT (₹)</Label>
              <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5 font-mono-num"
                data-testid="pay-amount-input" autoFocus />
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">DATE</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5"
                data-testid="pay-date-input" />
            </div>
          </div>

          {kind === "salary" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">MONTH</Label>
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                  className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5 font-mono-num"
                  data-testid="pay-month-input" />
              </div>
              <div>
                <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">
                  ADJUST FROM ADVANCE (₹, OPTIONAL)
                </Label>
                <Input type="number" min="0" step="0.01" max={staff?.balances?.advance_outstanding || 0}
                  value={adjustFromAdv} onChange={(e) => setAdjustFromAdv(e.target.value)}
                  className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5 font-mono-num"
                  data-testid="pay-adjust-input" placeholder="0" />
              </div>
            </div>
          )}

          {kind === "commission" && (
            <>
              <div>
                <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">LINKED ORDER (OPTIONAL)</Label>
                <Input value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder="Search by order # / customer / mobile"
                  className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5"
                  data-testid="pay-order-search" />
                {orderResults.length > 0 && !linkedOrderId && (
                  <div className="mt-1 max-h-44 overflow-y-auto rounded-sm border border-slate-700 bg-slate-900" data-testid="pay-order-results">
                    {orderResults.map((o) => (
                      <button key={o.id} type="button"
                        onClick={() => { setLinkedOrderId(o.id); setOrderSearch(`#${o.order_number} · ${o.customer_name}`); setOrderResults([]); }}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-slate-800 border-b border-slate-800 last:border-0">
                        <span className="font-mono-num text-orange-400">#{o.order_number}</span>
                        <span className="text-slate-200 ml-2">{o.customer_name}</span>
                        <span className="text-slate-500 ml-2">· {inr(o.total_amount)}</span>
                      </button>
                    ))}
                  </div>
                )}
                {linkedOrderId && (
                  <div className="mt-1 flex items-center gap-2 text-[11px] font-mono-num text-cyan-300">
                    Linked ✓
                    <button type="button" onClick={() => { setLinkedOrderId(""); setOrderSearch(""); }}
                      className="text-slate-500 hover:text-red-400"><XIcon size={12} /></button>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">COMMISSION % (OPTIONAL)</Label>
                <Input type="number" min="0" step="0.01" value={commissionPct} onChange={(e) => setCommissionPct(e.target.value)}
                  placeholder="e.g. 2 (for 2%)"
                  className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5 font-mono-num"
                  data-testid="pay-commission-pct" />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">FROM ACCOUNT</Label>
              <Select value={financialAccountId} onValueChange={setFinancialAccountId}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5" data-testid="pay-account-select">
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.account_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">METHOD</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5" data-testid="pay-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {["Cash", "UPI", "Bank Transfer", "Cheque", "Other"].map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">NOTE</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder="Anything to remember…"
              className="bg-slate-800/60 border-slate-600 text-white rounded-sm mt-1.5"
              data-testid="pay-note-input" />
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <Button type="button" onClick={onClose} variant="outline"
              className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800 h-11">Cancel</Button>
            <Button type="submit" disabled={busy}
              className="btn-industrial rounded-sm h-11 px-6 font-heading tracking-widest"
              data-testid="pay-submit">
              {busy ? "SAVING…" : "RECORD"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
